import React, { useState } from 'react';
import { useSchool } from '../context/SchoolContext';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { GraduationCap, Package, DollarSign, TrendingUp, AlertTriangle } from 'lucide-react';
import LossModal from '../components/LossModal';
import './Dashboard.css';

const Dashboard = () => {
    const {
        totalStudents = 0,
        activePackages = 0,
        monthlyIncome = 0,
        runwayMonths = 0,
        packageDistribution = [],
        statusDistribution = [],
        stageDistribution = [],
        financialData = [],
        branchPerformance = [],
        lossMakingStudents = []
    } = useSchool() || {};

    const [isLossModalOpen, setIsLossModalOpen] = useState(false);

    // Chart colors
    const STATUS_COLORS = ['#10b981', '#f59e0b', '#ef4444'];
    const PIPELINE_COLORS = ['#8b5cf6', '#6366f1', '#3b82f6', '#10b981'];
    const COLORS = ['#3b82f6', '#10b981', '#f59e0b'];

    // The KPICard component definition is removed as the instruction provides direct JSX for KPI cards.
    // If the KPICard component was intended to be kept and modified, the instruction was ambiguous.
    // Based on the provided code, the KPICard component is no longer used, and its content is inlined.

    return (
        <div className="dashboard">
            {/* 1. KPIs Section */}
            <div className="kpi-grid">
                {/* ... (KPI Cards stay at the top) */}
                <div className="kpi-card">
                    <div className="kpi-header">
                        <div className="kpi-text">
                            <p className="kpi-title">Total Students</p>
                            <h3 className="kpi-value">{totalStudents.toLocaleString()}</h3>
                            <div className="kpi-trend positive">
                                <TrendingUp size={12} />
                                <span>+12%</span>
                            </div>
                        </div>
                        <div className="kpi-icon icon-yellow">
                            <GraduationCap size={24} />
                        </div>
                    </div>
                    <p className="kpi-subtitle">vs last month</p>
                </div>
                <div className="kpi-card">
                    <div className="kpi-header">
                        <div className="kpi-text">
                            <p className="kpi-title">Active Packages</p>
                            <h3 className="kpi-value">{activePackages}</h3>
                            <div className="kpi-trend positive">
                                <TrendingUp size={12} />
                                <span>+5%</span>
                            </div>
                        </div>
                        <div className="kpi-icon icon-gold">
                            <Package size={24} />
                        </div>
                    </div>
                    <p className="kpi-subtitle">Current learners</p>
                </div>
                <div className="kpi-card">
                    <div className="kpi-header">
                        <div className="kpi-text">
                            <p className="kpi-title">Monthly Income</p>
                            <h3 className="kpi-value">${monthlyIncome.toLocaleString()}</h3>
                            <div className="kpi-trend positive">
                                <TrendingUp size={12} />
                                <span>+8%</span>
                            </div>
                        </div>
                        <div className="kpi-icon icon-green">
                            <DollarSign size={24} />
                        </div>
                    </div>
                    <p className="kpi-subtitle">Gross revenue</p>
                </div>
                <div className="kpi-card">
                    <div className="kpi-header">
                        <div className="kpi-text">
                            <p className="kpi-title">Est. Runway</p>
                            <h3 className="kpi-value">{runwayMonths} Mo</h3>
                            <div className="kpi-trend">
                                <span>Stable</span>
                            </div>
                        </div>
                        <div className="kpi-icon icon-red">
                            <AlertTriangle size={24} />
                        </div>
                    </div>
                    <p className="kpi-subtitle">Based on avg burn</p>
                </div>
            </div>

            {/* 2. Financial Overview Section (Now Wide and at the Top) */}
            <div className="chart-card-wide">
                <h3 className="chart-title">Financial Overview & Projection</h3>
                <div style={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer>
                        <LineChart data={financialData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" axisLine={false} tickLine={false} />
                            <YAxis axisLine={false} tickLine={false} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="income" stroke="#fbbf24" strokeWidth={3} name="Income" />
                            <Line type="monotone" dataKey="expenditure" stroke="#1f2937" strokeWidth={3} name="Expenditure" />
                            <Line type="monotone" dataKey="projection" stroke="#9ca3af" strokeDasharray="5 5" name="Projection" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* 3. Operational Sections Grid */}
            <div className="charts-grid-large">
                {/* Student Progression Portfolio (Pipeline + Stage Bar Chart) */}
                <div className="chart-card">
                    <h3 className="chart-title">Student Progression Pipeline</h3>
                    <div className="pipeline-container">
                        <div style={{ width: '180px', height: '180px' }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={stageDistribution}
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {stageDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIPELINE_COLORS[index % PIPELINE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="pipeline-legend">
                            {stageDistribution.map((entry, index) => (
                                <div key={entry.name} className="pipeline-item">
                                    <div className="pipeline-dot" style={{ backgroundColor: PIPELINE_COLORS[index] }}></div>
                                    <span className="pipeline-name">{entry.name}</span>
                                    <span className="pipeline-value">{entry.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="chart-card">
                    <h3 className="chart-title">Training Stage Breakdown</h3>
                    <div style={{ width: '100%', height: 250 }}>
                        <ResponsiveContainer>
                            <BarChart data={stageDistribution}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                <YAxis axisLine={false} tickLine={false} />
                                <Tooltip />
                                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                    {stageDistribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={PIPELINE_COLORS[index % PIPELINE_COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* 4. Secondary Distributions Section */}
            <div className="charts-grid">
                <div className="chart-card">
                    <h3 className="chart-title">Students by Package</h3>
                    <div style={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={packageDistribution}
                                    innerRadius={50}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {packageDistribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="chart-legend">
                        {packageDistribution.map((entry, index) => (
                            <div key={entry.name} className="legend-item">
                                <div className="legend-dot" style={{ backgroundColor: COLORS[index] }}></div>
                                <span>{entry.name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="chart-card">
                    <h3 className="chart-title">Student Status</h3>
                    <div style={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={statusDistribution}
                                    innerRadius={50}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {statusDistribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="chart-legend">
                        {statusDistribution.map((entry, index) => (
                            <div key={entry.name} className="legend-item">
                                <div className="legend-dot" style={{ backgroundColor: STATUS_COLORS[index] }}></div>
                                <span>{entry.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 5. Performance Table */}
            <div className="performance-card">
                {/* ... (Performance table stays same) */}
                <div className="performance-header">
                    <h3 className="chart-title">Branch Performance Overview</h3>
                    <button className="view-all-btn">Detailed Analysis</button>
                </div>
                <div className="performance-table">
                    <div className="table-header">
                        <div>Branch</div>
                        <div>Students</div>
                        <div>Revenue</div>
                        <div>Status</div>
                    </div>
                    {branchPerformance.map((branch) => (
                        <div key={branch.branch} className="table-row">
                            <div className="branch-name">{branch.branch}</div>
                            <div>{branch.students}</div>
                            <div className="revenue">${branch.revenue.toLocaleString()}</div>
                            <div>
                                <span className={`status-badge ${branch.status.toLowerCase().replace(' ', '-')}`}>
                                    {branch.status}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 6. Alerts Section (Now at bottom) */}
            <div className="alerts-section">
                <h3 className="section-title">Critical Alerts</h3>
                <div className="alerts-grid">
                    <div className="alert-card alert-danger">
                        <div className="alert-icon">
                            <TrendingUp size={24} />
                        </div>
                        <div className="alert-content">
                            <h4 className="alert-title">Loss-Making Students</h4>
                            <p className="alert-text">{lossMakingStudents.length} Students have exceeded package lesson limits.</p>
                            <button className="alert-btn" onClick={() => setIsLossModalOpen(true)}>View Details</button>
                        </div>
                    </div>
                    <div className="alert-card alert-warning">
                        <div className="alert-icon">
                            <TrendingUp size={24} />
                        </div>
                        <div className="alert-content">
                            <h4 className="alert-title">Revenue Target Missed</h4>
                            <p className="alert-text">Gweru Branch is 15% below monthly target.</p>
                            <button className="alert-btn" style={{ background: '#d97706' }}>Investigate</button>
                        </div>
                    </div>
                </div>
            </div>
            <LossModal
                isOpen={isLossModalOpen}
                onClose={() => setIsLossModalOpen(false)}
                students={lossMakingStudents}
            />
        </div>
    );
};

export default Dashboard;
